import React, { useState } from 'react';
import { Mail, Clock, CheckCircle } from 'lucide-react';
import { COMING_SOON_CONFIG } from '../../utils/featureFlags';
import { useMonitoring } from '../../hooks/useMonitoring';

const ComingSoonBanner = ({ variant = 'banner' }) => {
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const { trackUserEvent, trackConversionEvent } = useMonitoring();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setIsLoading(true);
    setError(null);
    setSuccess(false);
    // Track the conversion event
    trackConversionEvent('coming_soon_signup', null, { email });
    trackUserEvent('email_signup', { source: 'coming_soon_banner' });

    try {
      const response = await fetch("https://formspree.io/f/mvgrnyzk", {
        method: "POST",
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          source: 'coming_soon_banner',
        }),
      });
      if (response.ok) {
        setIsSubmitted(true);
        setEmail('');
      } else {
        setError('Sorry, there was a problem subscribing. Please try again later.');
      }
    } catch (error) {
      setError('Sorry, there was a problem subscribing. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  if (variant === 'modal') {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-md w-full p-6 text-center">
          <Clock className="mx-auto h-12 w-12 text-blue-600 mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Coming Soon!</h2>
          <p className="text-gray-600 mb-6">{COMING_SOON_CONFIG.message}</p>
          
          {error && (
            <div className="text-red-600 mb-4">
              {error}
            </div>
          )}
          
          {!isSubmitted ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={COMING_SOON_CONFIG.emailPlaceholder}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Subscribing...' : COMING_SOON_CONFIG.ctaText}
              </button>
            </form>
          ) : (
            <div className="text-green-600">
              <CheckCircle className="mx-auto h-8 w-8 mb-2" />
              <p>{COMING_SOON_CONFIG.successMessage}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-4 px-4">
      <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between">
        <div className="flex items-center mb-4 sm:mb-0">
          <Clock className="h-5 w-5 mr-2" />
          <span className="font-medium">
            Launching {COMING_SOON_CONFIG.estimatedLaunch} - {COMING_SOON_CONFIG.message}
          </span>
        </div>
        
        {error && (
          <div className="text-red-600 mb-4">
            {error}
          </div>
        )}
        
        {!isSubmitted ? (
          <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={COMING_SOON_CONFIG.emailPlaceholder}
              className="px-4 py-2 rounded-lg text-gray-900 focus:ring-2 focus:ring-white focus:outline-none"
              required
            />
            <button
              type="submit"
              disabled={isLoading}
              className="bg-white text-blue-600 px-6 py-2 rounded-lg font-medium hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Subscribing...' : COMING_SOON_CONFIG.ctaText}
            </button>
          </form>
        ) : (
          <div className="flex items-center text-green-300">
            <CheckCircle className="h-5 w-5 mr-2" />
            <span>{COMING_SOON_CONFIG.successMessage}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default ComingSoonBanner; 